import { useState, useEffect,useMemo } from 'react';
import { useDashboardData } from './useDashboard';
import userProfileStore from '../core/UserProfileStore';
import useUserSettingStore from './UserSettingStore';
import {accountingGLUrl,accountingGLBalanceAppLinkCommonUrl, accountingGLUnpostedAppLinkCommonUrl} from './DashboardConfig';
import { APP_ENV,  } from '../../constants';
import {useGLDashboardFilter} from './filters/GLFilter';
import moment from 'moment';

export const GL_DASHBOARD = {
  DASHBOARD_ID: 'gl-dashboard',
  DOMAIN_ID: 'accounting',
  WIDGET_UNPOSTED_TRANS_ID: 'gl-unposted-transaction',
  WIDGET_UNPOSTED_TRANS_ERR_ID: 'gl-unposted-error',
  WIDGET_ACCT_BAL_ID: 'gl-account-balance',
  QUERY_UNPOSTED_ID: 'gl-unposted-query',
  QUERY_ACCT_BAL_ID: 'gl-acct-bal-query',
};

export const getWidgets = (t:any) => {
return [
  {
    id: GL_DASHBOARD.WIDGET_UNPOSTED_TRANS_ID,
    dashboardId:GL_DASHBOARD.DASHBOARD_ID,
    dataSourceId: GL_DASHBOARD.QUERY_UNPOSTED_ID,
    isFilterAvailable: true,
    title: t('dashboard.accounting.unpostedTransaction'),
    defaultView: 'metrics',
    availableViews: ['metrics'],
    link:accountingGLUnpostedAppLinkCommonUrl[APP_ENV as unknown as keyof typeof accountingGLUnpostedAppLinkCommonUrl],
    thresholdFields: [{ name: 'Unposted Transaction' }],
    dataMapping: {
      transform: (sourceData: any) => {
        return {
          name: 'Unposted Transaction',
          count: sourceData[0]?.length ?? 0,
        };
      },
    },
  },
  {
    id: GL_DASHBOARD.WIDGET_UNPOSTED_TRANS_ERR_ID,
    dashboardId:GL_DASHBOARD.DASHBOARD_ID,
    dataSourceId: GL_DASHBOARD.QUERY_UNPOSTED_ID,
    isFilterAvailable: true,
    title: t('dashboard.accounting.unPostedTransactionError'),
    defaultView: 'metrics',
    link:accountingGLUnpostedAppLinkCommonUrl[APP_ENV as unknown as keyof typeof accountingGLUnpostedAppLinkCommonUrl],
    availableViews: ['metrics'],
    thresholdFields: [{ name: 'Unposted Transaction Error' }],
    showPercentage: true,
    dataMapping: {
      transform: (sourceData: any) => {
       const errored = sourceData[0]?.filter((s:any) => s.isContainErrors );
          return {
            name: 'Error',
            count: errored?.length ?? 0,
            total: sourceData[0]?.length ?? 0,
            trend: `of ${sourceData[0]?.length ?? 0} unposted transactions`
          };
      },
    },
  },
  {
    id: GL_DASHBOARD.WIDGET_ACCT_BAL_ID,
    dashboardId:GL_DASHBOARD.DASHBOARD_ID,
    dataSourceId: GL_DASHBOARD.QUERY_ACCT_BAL_ID,
    isFilterAvailable: true,
    type: 'multiview',
    height:'300px',
    title: t('dashboard.accounting.acctBalance'),
    defaultView: 'stacked-bar',
    isPricing: true,
    link:accountingGLBalanceAppLinkCommonUrl[APP_ENV as unknown as keyof typeof accountingGLBalanceAppLinkCommonUrl],
    dataKey: [
      'Balance Forward',
      'Current Month Balance',
      'Future Month Balance',
    ],
    colors: {
      'Balance Forward': '#2C4F81',
      'Current Month Balance': '#457CCF',
      'Future Month Balance': '#8AABDC',
    },
    xKey: 'name',
    thresholdFields: [
      { name: 'Balance Forward' },
      { name: 'Current Month Balance' },
      { name: 'Future Month Balance' },
    ],
    availableViews: ['stacked-bar', 'table', 'grid'],
    columnConfig: {
      name: { width: '300px', minWidth: '200px', maxWidth: '500px', resizable: true },
    },
    dataMapping: {
      transform: (sourceData: any) => {
        const accounts = sourceData[1]?.accountBalances ?? [];
        const data = accounts
          .sort(
            (a: any, b: any) =>
              Math.abs(b.totalBalance) - Math.abs(a.totalBalance)
          )
          .slice(0, 10)
          .map((acc: any) => ({
            name: acc.companyId+"-"+ acc.accountNo,
            fullName: acc.accountNo,
            storeId: acc.storeId,
            amount: (acc.totalBalance),
            'Balance Forward': (acc.forwardBalance),
            'Current Month Balance': (acc.currentMonthBalance),
            'Future Month Balance': (acc.futureMonthBalance),
            totalBalance: (acc.totalBalance),
            total: (acc.totalBalance),
          }));
        return data;
      },
    },
  },
];
}


export const getGLDashboardConfig = (
  enterpriseId: string,
  storeId: string,
  body: any
) => {
  const config = [];
  config.push(
    {
      id: GL_DASHBOARD.QUERY_UNPOSTED_ID,
      name: 'Overview',
      layout: 'default',
      apiConfig: {
        method:'GET',
        headers:{
          'x-enterprise-id': enterpriseId,
          'x-store-id': storeId[0],
          'x-department-name': body.accountName,
          'x-department-type': 'A',
        },
        path:"/gl-documents/unposted",
        endpoint: accountingGLUrl[APP_ENV as unknown as keyof typeof accountingGLUrl]
      },
  });
  config.push({
    id: GL_DASHBOARD.QUERY_ACCT_BAL_ID,
    name: 'Overview',
    layout: 'default',
    apiConfig: {
      method: 'POST',
      headers: {
        'x-enterprise-id': enterpriseId,
        'x-store-id': storeId[0],
        'cora-account': body.accountName,
        'x-department-name': body.accountName,
        'x-department-type': 'A',
      },
      body: {
        group: body.groupId,
        glGroupIds: body.glGroupIds,
        accounts: body.accounts,
        companyAccountIds: body.companyAccountIds,
        dailyBalanceDate: body.dailyBalanceDate,
        yearType: 'CALENDAR',
      },
      path: '/gl-inquiry/account-balances',
      endpoint: accountingGLUrl[APP_ENV as unknown as keyof typeof accountingGLUrl],
    },
  });
  return config;
};

export const createQueryBody = (
  defaultGroup: any,
  filteredAccountData: any,
  date: any
) => {
  const accountName =
    defaultGroup?.name?.trim().match(/^([A-Z0-9]+-[A-Z0-9]+)/)?.[1] ?? null;
  const groupId = defaultGroup?.groupId;
  const glGroupIds = defaultGroup?.glMap?.map((glgroup: any) => ({
    companyGroupId: glgroup.groupId,
    storeId: glgroup.organizationUnits[0],
  }));
  const accounts = filteredAccountData?.map((acct: any) => acct.account_number);
  const companyAccountIds = filteredAccountData?.map((acct: any) => {
    const glGroup = defaultGroup.glMap?.find(
      (gl: any) => gl.companyId === acct.company_id
    );
    return {
      accountNo: acct?.account_number,
      companyId: acct?.company_id,
      storeId: glGroup?.organizationUnits[0],
      companyGroupId: glGroup?.groupId,
    };
  });

  const dailyBalanceDate = date;
  const yearType = 'CALENDAR';

  const acctBalExist =
    !!accountName &&
    !!groupId &&
    !!glGroupIds &&
    !!accounts &&
    !!companyAccountIds &&
    !!dailyBalanceDate &&
    !!yearType;

  return {
    acctBalExist,
    accountName,
    groupId,
    glGroupIds,
    accounts,
    companyAccountIds,
    dailyBalanceDate,
    yearType,
  };
};


export const useGLDashboard = ({ enabled = true, filteredWidgets = [] }: any = {}) => {

  const { userSetting } = useUserSettingStore();
  const { enterpriseId } = userProfileStore();
  const { 
        filterSetting, 
        defaultAccounts, 
        glDate, 
        isLoading: isLoadingFilter 
    } = useGLDashboardFilter({ userSetting, enabled });

  const [widgets, setWidgets] = useState<any[]>(filteredWidgets);

  const config = useMemo(() => {
      if (!filterSetting?.group?.groupId || !enterpriseId || !glDate) {
          return null;
      }

      const accounts = (filterSetting.accounts && filterSetting.accounts.length > 0) 
          ? filterSetting.accounts 
          : defaultAccounts;

      if (!accounts || accounts.length === 0) return null;

      const { group, storeIds } = filterSetting;
      
      // Calculate date: use reportDate if available, otherwise calculate from reportDaysAgo
      let date;
      if (filterSetting.reportDate) {
          date = filterSetting.reportDate;
      } else if (filterSetting.reportDaysAgo !== undefined) {
          // Convert to number since it may come as a string
          const daysAgo = Number(filterSetting.reportDaysAgo);
          
          if (daysAgo === 0) {
              // Current Period: Use filterSetting.date (calendar date) or glDate as fallback
              date = filterSetting.date || glDate;
          } else if (daysAgo === 1) {
              // Today
              date = moment().format('YYYY-MM-DD');
          } else {
              // X days ago: subtract the exact number of days
              date = moment().subtract(daysAgo, 'days').format('YYYY-MM-DD');
          }
      } else {
          // Default: Use Current Period (filterSetting.date or glDate)
          date = filterSetting.date || glDate;
      }
      
      const body = createQueryBody(group, accounts, date);
      return getGLDashboardConfig(enterpriseId, storeIds, body);

      //eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
      filterSetting?.group?.groupId, 
      filterSetting?.accounts, // Watch the array reference
      filterSetting?.reportDate,
      filterSetting?.reportDaysAgo,
      enterpriseId, 
      defaultAccounts,
      glDate
  ]);

  const { isLoading:isDataLoading, error, getWidgetData, getWidgetError } = useDashboardData({ config, enabled: enabled && !!config });


  // Initialize widgets
  useEffect(() => {
    const dashboard = userSetting.dashboard?.find((d: any) => d.id === GL_DASHBOARD.DASHBOARD_ID);
        const savedWidgets = dashboard?.widgets || [];
        const updatedWidgets = filteredWidgets
          .map((dwidget: any) => {
            const saved = savedWidgets.find((w: any) => w.id === dwidget.id);
            return {
                ...dwidget,
                dashboardId: GL_DASHBOARD.DASHBOARD_ID,
                fields: saved?.fields || dwidget.fields,
                view: saved?.view || dwidget.view,
            };
        });
        setWidgets(updatedWidgets);
  }, [filteredWidgets, userSetting]);

  // Get the complete filter from userSetting to preserve all properties
  const dashboardFilter = userSetting?.dashboard?.find((d: any) => d.id === GL_DASHBOARD.DASHBOARD_ID)?.filter || {};

  return {
    dashboardId:GL_DASHBOARD.DASHBOARD_ID,
    widgets,
    filter: dashboardFilter, // Return complete filter from userSetting
    isLoading: enabled ? (isLoadingFilter || isDataLoading) : false,
    error ,
    getWidgetData,
    getWidgetError,
  };
};